/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
/**
 * Bán hàng & tài chính › Dịch vụ (ADR-026 §6): the services customers bought, filtered by state
 * or a search, and a service's page: where it stands against its due date, its paid periods, its
 * renewal orders (with the VietQR transfer code), the steps of its overdue calendar, its history
 * and staff's requests; and the operations staff may take on it (extend, suspend, unsuspend,
 * terminate), each with a reason, shown only to who holds the permission. The open service lives
 * in the URL (`service`).
 */
import { ArrowLeft, Ban, CalendarPlus, CirclePause, CirclePlay, CircleX, CheckCircle2 } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from '@/lib/scopedParams';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { api } from '../../api';
import type { Subscription, SubscriptionDetail, SubscriptionState, SubscriptionFilter, WaitingPaidItem, Timing } from '../../api/areas/subscriptions';
import { Badge, type Tone } from '../../components/Badge';
import { Callout, DataTable, Field, Section, type Column } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { KeyValue } from '../../components/KeyValue';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { Loading } from '../../components/States';
import { useWrite, WriteError } from '../../components/Write';
import { useAccess } from '../../lib/access';
import { formatDate, formatDateTime } from '../../lib/format';
import { dueStanding, extensionPermissions, LARGE_EXTENSION_DAYS, operationsFor, STAFF_SUSPEND_REASONS, STATE_TONE, SUBSCRIPTION_STATES, vietnamToday, vnd, type StaffOperation } from '../../lib/subscriptions';
import { useApi } from '../../lib/useApi';
import { ActionDialog, FormFooter, ReasonField, selectClass, type DialogBodyProps } from '../operator/shared';
import { HostingPanel, ResolveOperation } from './HostingPanel';
import { DnsZoneEditor } from '../domains/tabs/DnsZone';

function useSubT() {
  const { t, i18n } = useTranslation('subscriptions');
  const en = i18n.language.startsWith('en');
  return { t, en };
}

function StateBadge({ s }: { s: Pick<Subscription, 'state' | 'suspend_reason'> }) {
  const { t } = useSubT();
  const label = t(`state.${s.state}`);
  return (
    <Badge tone={STATE_TONE[s.state]} title={s.state}>
      {s.state === 'SUSPENDED' && s.suspend_reason ? `${label} · ${t(`suspendReason.${s.suspend_reason}`)}` : label}
    </Badge>
  );
}

function Due({ s }: { s: Pick<Subscription, 'state' | 'next_due_on'> }) {
  const { t } = useSubT();
  const d = dueStanding(s, vietnamToday());
  if (d.key === 'none' || d.key === 'ended') return <span className="text-muted-foreground">{t(`due.${d.key}`)}</span>;
  return (
    <span className={d.key === 'overdue' ? 'text-destructive' : undefined}>
      {formatDate(s.next_due_on)}
      <span className="block text-xs text-muted-foreground">{t(`due.${d.key}`, { count: d.days })}</span>
    </span>
  );
}

/** The tone of a provisioning operation's status. */
const OP_TONE: Record<string, Tone> = { QUEUED: 'pending', SENDING: 'pending', RUNNING: 'pending', SUCCEEDED: 'ok', REFUSED: 'crit', FAILED: 'crit', UNKNOWN: 'warn', CANCELLED: 'neutral' };

function useLoose() {
  const { t } = useSubT();
  return t as unknown as (key: string, o?: Record<string, unknown>) => string;
}

function OpStatusBadge({ status }: { status: string }) {
  const loose = useLoose();
  return (
    <Badge tone={OP_TONE[status] ?? 'neutral'} title={status}>
      {loose(`opStatus.${status}`, { defaultValue: status })}
    </Badge>
  );
}

/** "Chờ cấp phát": why a service waits — its latest operation at the provider, or its stopped delivery. */
function DeliveryStatus({ s }: { s: Subscription }) {
  const { t } = useSubT();
  const loose = useLoose();
  const op = s.last_operation;
  if (op) {
    const why = [op.refusal_code ?? op.attention ?? op.failure_class, op.message].filter(Boolean).join(' · ');
    return (
      <span className="grid gap-1">
        <span className="flex flex-wrap items-center gap-1">
          {loose(`opName.${op.op}`, { defaultValue: op.op })} <OpStatusBadge status={op.status} />
        </span>
        <span className="text-xs text-muted-foreground">
          {t('delivery.attempts', { count: op.attempts })}
          {why ? ` · ${why}` : ''}
        </span>
      </span>
    );
  }
  if (s.workflow?.failure_code) return <span className="text-destructive">{t('delivery.workflow', { code: s.workflow.failure_code })}</span>;
  return <span className="text-muted-foreground">{t('delivery.none')}</span>;
}

type Operation = SubscriptionDetail['operations'][number];

/** "Nhật ký cấp phát": the kernel's operations of a provisioned service; a hosting one's unknown result is resolved here. */
function OperationsSection({ s, onChanged }: { s: SubscriptionDetail; onChanged: () => void }) {
  const { t } = useSubT();
  const loose = useLoose();
  const access = useAccess();
  const canResolve = s.branch === 'hosting' && Boolean(access.grants?.some((g) => g.scope === 'Global' && g.permissions.includes('subscription.operate')));
  const columns: Column<Operation>[] = [
    { key: 'op', header: t('opCol.op'), cell: (o) => loose(`opName.${o.op}`, { defaultValue: o.op }) },
    { key: 'status', header: t('opCol.status'), cell: (o) => <OpStatusBadge status={o.status} /> },
    { key: 'attempts', header: t('opCol.attempts'), numeric: true, cell: (o) => o.attempts },
    { key: 'detail', header: t('opCol.detail'), cell: (o) => [o.refusal_code, o.message, o.failure_class, o.attention].filter(Boolean).join(' · ') || '—' },
    { key: 'at', header: t('opCol.at'), cell: (o) => formatDateTime(o.created_at) },
    { key: 'finished', header: t('opCol.finished'), cell: (o) => (o.finished_at ? formatDateTime(o.finished_at) : '—') },
  ];
  if (canResolve) {
    columns.push({
      key: 'action',
      header: t('opCol.action'),
      cell: (o) => (o.status === 'UNKNOWN' || o.status === 'RUNNING' ? <ResolveOperation operation={o} serviceId={s.id} onChanged={onChanged} /> : null),
    });
  }
  return (
    <Section title={t('detail.operations')} description={t('detail.operationsHint')} flush>
      <DataTable columns={columns} rows={s.operations} rowKey={(o) => o.id} empty={t('detail.none')} />
    </Section>
  );
}

/** `/sales?tab=subscriptions`: the list, or one service (`service`). */
export function SubscriptionsPage() {
  const { t } = useSubT();
  useDocumentTitle(t('title'));
  const [params, setParams] = useSearchParams();
  const open = params.get('service');
  const go = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v) next.set(k, v);
      else next.delete(k);
    }
    setParams(next);
  };
  if (open) return <ServiceView id={open} onBack={() => go({ service: null })} />;
  const state = (params.get('state') as SubscriptionState | null) ?? null;
  const selected = params.get('view');
  const view = VIEWS.find((v) => v === selected) ?? 'all';
  const rawOffset = Number(params.get('offset') ?? 0);
  const offset = Number.isSafeInteger(rawOffset) && rawOffset >= 0 ? rawOffset : 0;
  const rawDays = Number(params.get('days') ?? 30);
  const days = Number.isInteger(rawDays) && rawDays >= 0 && rawDays <= 365 ? rawDays : 30;
  return <ServiceList key={`${view}:${params.get('q') ?? ''}`} view={view} days={days} offset={offset} filter={{ state, q: params.get('q') ?? '', customer_id: params.get('customer_id'), group: params.get('group') }} onFilter={go} onOpen={(id) => go({ service: id })} />;
}

const VIEWS = ['all', 'waiting', 'due', 'overdue'] as const;
const PAGE_SIZE = 50;
const QUEUE_LIMIT = 500;

function ServiceList({ view, days, offset, filter, onFilter, onOpen }: { view: (typeof VIEWS)[number]; days: number; offset: number; filter: SubscriptionFilter; onFilter: (p: Record<string, string | null>) => void; onOpen: (id: string) => void }) {
  const { t, en } = useSubT();
  const { state, q, customer_id, group } = filter;
  const [text, setText] = useState(q ?? '');
  const list = useApi<{ items: Subscription[]; paid_items: WaitingPaidItem[] }>(async (signal) => {
    if (view === 'waiting') {
      const result = await api.listWaitingServices({ limit: QUEUE_LIMIT }, { signal });
      return { items: result.services, paid_items: result.paid_items };
    }
    if (view === 'due') return { ...await api.listDueServices({ days, limit: QUEUE_LIMIT }, { signal }), paid_items: [] };
    if (view === 'overdue') return { ...await api.listOverdueServices({ limit: QUEUE_LIMIT }, { signal }), paid_items: [] };
    return { ...await api.listSubscriptions({ state, q, customer_id, group, limit: PAGE_SIZE, offset }, { signal }), paid_items: [] };
  }, [view, state, q, customer_id, group, days, offset]);
  const columns: Column<Subscription>[] = [
    {
      key: 'service',
      header: t('col.service'),
      cell: (s) => (
        <button type="button" className="text-left font-medium text-primary underline-offset-4 hover:underline" onClick={() => onOpen(s.id)}>
          {s.label}
          <span className="block text-xs font-normal text-muted-foreground">{en ? s.product_name_en : s.product_name_vi}</span>
        </button>
      ),
    },
    { key: 'customer', header: t('col.customer'), cell: (s) => s.customer_name },
    { key: 'plan', header: t('col.plan'), cell: (s) => `${en ? s.plan_name_en : s.plan_name_vi} · ${t(`cycle.${s.cycle}`)}` },
    { key: 'state', header: t('col.state'), cell: (s) => <StateBadge s={s} /> },
    { key: 'due', header: t('col.due'), cell: (s) => <Due s={s} /> },
    { key: 'amount', header: t('col.amount'), numeric: true, cell: (s) => vnd(s.recurring_amount) },
  ];
  if (view === 'waiting') columns.splice(4, 0, { key: 'delivery', header: t('delivery.col'), cell: (s) => <DeliveryStatus s={s} /> });
  return (
    <>
      <PageHeader title={t('title')} crumbs={[{ label: t('crumb') }]} subtitle={t('subtitle')} />
      <nav className="mb-4 flex flex-wrap gap-2" aria-label={t('views.label')}>
        {VIEWS.map((v) => <Button key={v} variant={view === v ? 'default' : 'outline'} aria-current={view === v ? 'page' : undefined} onClick={() => onFilter({ view: v === 'all' ? null : v, offset: null })}>{t(`views.${v}`)}</Button>)}
      </nav>
      {view === 'all' ? <div className="mb-4 flex flex-wrap items-end gap-3">
        <form
          className="flex gap-2"
          role="search"
          onSubmit={(e) => {
            e.preventDefault();
            onFilter({ q: text.trim() || null, offset: null });
          }}
        >
          <Input value={text} onChange={(e) => setText(e.target.value)} placeholder={t('search')} aria-label={t('search')} className="w-72" />
        </form>
        <select className={selectClass} value={state ?? ''} aria-label={t('col.state')} onChange={(e) => onFilter({ state: e.target.value || null, offset: null })}>
          <option value="">{t('allStates')}</option>
          {SUBSCRIPTION_STATES.map((s) => (
            <option key={s} value={s}>
              {t(`state.${s}`)}
            </option>
          ))}
        </select>
      </div> : null}
      {view === 'due' ? <div className="mb-4"><Field label={t('views.days')}><select className={selectClass} value={days} onChange={(e) => onFilter({ days: e.target.value, offset: null })}>{[...new Set([7, 14, 30, 60, 90, days])].sort((a, b) => a - b).map((n) => <option key={n} value={n}>{t('views.withinDays', { count: n })}</option>)}</select></Field></div> : null}
      {view !== 'all' && list.status === 'success' && (list.data.items.length >= QUEUE_LIMIT || list.data.paid_items.length >= QUEUE_LIMIT) ? <Callout tone="warn" className="mb-4">{t('views.limit', { count: QUEUE_LIMIT })}</Callout> : null}
      <Section flush>
        {list.status === 'loading' ? <Loading rows={5} /> : null}
        {list.status === 'error' ? <DataError error={list.error} onRetry={list.reload} /> : null}
        {list.status === 'success' ? <>
          <DataTable columns={columns} rows={list.data.items} rowKey={(s) => s.id} empty={t('empty')} />
          {view === 'all' ? <div className="flex items-center justify-between gap-3 border-t p-4">
            <Button variant="outline" disabled={offset === 0} onClick={() => onFilter({ offset: offset > PAGE_SIZE ? String(offset - PAGE_SIZE) : null })}>{t('pager.previous')}</Button>
            <span className="text-sm text-muted-foreground">{t('pager.page', { n: Math.floor(offset / PAGE_SIZE) + 1 })}</span>
            <Button variant="outline" disabled={list.data.items.length < PAGE_SIZE} onClick={() => onFilter({ offset: String(offset + PAGE_SIZE) })}>{t('pager.next')}</Button>
          </div> : null}
        </> : null}
      </Section>
      {view === 'waiting' && list.status === 'success' && list.data.paid_items.length ? <Section title={t('views.paidItems')} className="mt-6" flush>
        <DataTable columns={[
          { key: 'service', header: t('col.service'), cell: (i) => <Link className="text-primary hover:underline" to={`/orders/${encodeURIComponent(i.order_id)}`}>{i.description ?? t('views.order')}</Link> },
          { key: 'customer', header: t('col.customer'), cell: (i) => i.customer_name },
          { key: 'amount', header: t('views.paidAmount'), cell: (i) => vnd(i.amount) },
          { key: 'workflow', header: t('views.workflow'), cell: (i) => i.workflow ? <Link className="text-primary hover:underline" to={`/ops/workflows/${encodeURIComponent(i.workflow.id)}`}>{t('views.openWorkflow')}</Link> : t('views.notStarted') },
        ] satisfies Column<WaitingPaidItem>[]} rows={list.data.paid_items} rowKey={(i) => i.order_item_id} />
      </Section> : null}
    </>
  );
}

type ServiceOperation = StaffOperation | 'completeDelivery' | 'cancelDelivery';
const OP_ICON = { extend: CalendarPlus, suspend: CirclePause, unsuspend: CirclePlay, terminate: CircleX, completeDelivery: CheckCircle2, cancelDelivery: Ban } as const;
/** Staff answer a manual delivery: delivered, or it cannot be (the order item fails, the funds go back). */
const DELIVERY_OPS = ['completeDelivery', 'cancelDelivery'] as const;
const isDeliveryOp = (op: ServiceOperation) => (DELIVERY_OPS as readonly ServiceOperation[]).includes(op);
const DESTRUCTIVE_OPS: readonly ServiceOperation[] = ['terminate', 'cancelDelivery'];

function ServiceView({ id, onBack }: { id: string; onBack: () => void }) {
  const { t, en } = useSubT();
  const access = useAccess();
  const one = useApi((signal) => api.getSubscription(id, { signal }), [id]);
  if (one.status === 'loading') return <Loading rows={6} />;
  if (one.status === 'error') return <DataError error={one.error} onRetry={one.reload} />;
  const s = one.data;
  const offered: { op: ServiceOperation; permission: string }[] = operationsFor(s);
  if (s.fulfilment === 'MANUAL' && s.state === 'PROVISIONING') offered.unshift(...DELIVERY_OPS.map((op) => ({ op, permission: 'subscription.operate' })));
  const ops = offered.filter((o) => access.grants?.some((g) => g.scope === 'Global' && g.permissions.includes(o.permission)));
  const l = s.lifecycle;
  const dayText = (day: number) => (day > 0 ? t('dayBefore', { count: day }) : day < 0 ? t('dayAfter', { count: -day }) : t('dayDue'));
  const reasonText = (r: string) => t(`reason.${r}`, { defaultValue: r });
  const loose = t as unknown as (key: string, o?: Record<string, unknown>) => string;
  return (
    <>
      <PageHeader
        title={s.label}
        crumbs={[{ label: t('crumb') }, { label: t('title') }]}
        subtitle={`${en ? s.product_name_en : s.product_name_vi} · ${en ? s.plan_name_en : s.plan_name_vi}`}
        badges={<StateBadge s={s} />}
        actions={
          <>
            <Button variant="outline" onClick={onBack}>
              <ArrowLeft aria-hidden="true" /> {t('back')}
            </Button>
            {ops.map(({ op }) => (
              <ActionDialog key={op} label={t(`op.${op}`)} icon={OP_ICON[op]} title={t(`op.${op}Title`, { label: s.label })} variant={DESTRUCTIVE_OPS.includes(op) ? 'destructive' : 'outline'} onChanged={one.reload}>
                {(p) => <OperationForm {...p} op={op} service={s} />}
              </ActionDialog>
            ))}
          </>
        }
      />
      <div className="flex flex-col gap-6">
        <Section title={t('detail.overview')}>
          <KeyValue
            columns={3}
            rows={[
              [t('detail.customer'), s.customer_name],
              [t('detail.product'), en ? s.product_name_en : s.product_name_vi],
              [t('detail.plan'), `${en ? s.plan_name_en : s.plan_name_vi} (${s.plan_code})`],
              [t('detail.cycle'), t(`cycle.${s.cycle}`)],
              [t('detail.state'), <StateBadge key="state" s={s} />],
              [t('detail.nextDue'), <Due key="due" s={s} />],
              [t('detail.anchor'), formatDate(s.anchor_on)],
              [t('detail.amount'), `${vnd(s.recurring_amount)} đ`],
              [t('detail.autoRenew'), s.auto_renew ? t('autoRenew.on') : t('autoRenew.off')],
              [t('detail.delivery'), t(`fulfilment.${s.fulfilment}`)],
              s.service ? [t('detail.service'), t(`service.${s.service.state}`)] : null,
              s.service?.paid_until ? [t('detail.paidUntil'), formatDate(s.service.paid_until)] : null,
              s.suspend_hold_until ? [t('detail.hold'), formatDate(s.suspend_hold_until)] : null,
            ]}
          />
          {l ? (
            <p className="mt-4 text-sm text-muted-foreground">
              {t('detail.lifecycle')}: {l.suspend_enabled || l.terminate_enabled ? t('detail.lifecycleText', { invoice: l.invoice_days_before, suspend: l.suspend_after_days, terminate: l.terminate_after_days }) : t('detail.lifecycleOff')}
            </p>
          ) : null}
          {s.service_state === 'UNKNOWN' ? <Callout tone="warn" className="mt-4">{t('service.UNKNOWN')}</Callout> : null}
        </Section>

        {s.branch === 'hosting' && s.fulfilment === 'PROVISIONED' ? <HostingPanel service={s} onChanged={one.reload} /> : null}
        {s.branch === 'dns' && s.fulfilment === 'PROVISIONED' && s.service ? <DnsZoneEditor zoneId={s.id} /> : null}

        {s.fulfilment === 'PROVISIONED' ? <OperationsSection s={s} onChanged={one.reload} /> : null}

        <Section title={t('detail.renewals')} flush>
          <DataTable
            columns={[
              { key: 'due', header: t('renewalCol.due'), cell: (r) => formatDate(r.due_on) },
              { key: 'state', header: t('renewalCol.state'), cell: (r) => <Badge tone={r.state === 'PAID' ? 'ok' : r.state === 'OPEN' ? 'pending' : 'neutral'} title={r.state}>{t(`renewal.${r.state}`)}</Badge> },
              { key: 'paidBy', header: t('renewalCol.paidBy'), cell: (r) => (r.paid_by ? t(`paidBy.${r.paid_by}`) : '—') },
              { key: 'amount', header: t('renewalCol.amount'), numeric: true, cell: (r) => vnd(r.amount) },
              { key: 'payment', header: t('renewalCol.payment'), cell: (r) => (r.payment?.code ? t('detail.qrCode', { code: r.payment.code }) : '—') },
            ] satisfies Column<SubscriptionDetail['renewals'][number]>[]}
            rows={s.renewals}
            rowKey={(r) => `${r.due_on}-${r.order_id}`}
            empty={t('detail.none')}
          />
        </Section>

        <Section title={t('detail.periods')} flush>
          <DataTable
            columns={[
              { key: 'kind', header: t('periodCol.kind'), cell: (p) => t(`period.${p.kind}`) },
              { key: 'from', header: t('periodCol.from'), cell: (p) => formatDate(p.from_on) },
              { key: 'to', header: t('periodCol.to'), cell: (p) => formatDate(p.to_on) },
              { key: 'amount', header: t('periodCol.amount'), numeric: true, cell: (p) => vnd(p.amount) },
              { key: 'reason', header: t('periodCol.reason'), cell: (p) => p.reason ?? '—' },
            ] satisfies Column<SubscriptionDetail['periods'][number]>[]}
            rows={s.periods}
            rowKey={(p) => p.id}
            empty={t('detail.none')}
          />
        </Section>

        <Section title={t('detail.calendar')} flush>
          <DataTable
            columns={[
              { key: 'due', header: t('calendarCol.due'), cell: (e) => formatDate(e.due_on) },
              { key: 'step', header: t('calendarCol.step'), cell: (e) => t(`step.${e.event}`) },
              { key: 'day', header: t('calendarCol.day'), cell: (e) => (e.event === 'REMINDER' || e.event === 'SUSPENDED' || e.event === 'LATE_FEE' ? dayText(e.event === 'REMINDER' ? e.day : -e.day) : '—') },
              { key: 'at', header: t('calendarCol.at'), cell: (e) => formatDateTime(e.at) },
            ] satisfies Column<SubscriptionDetail['calendar'][number]>[]}
            rows={s.calendar}
            rowKey={(e) => `${e.due_on}-${e.event}-${e.day}`}
            empty={t('detail.none')}
          />
        </Section>

        <Section title={t('detail.history')} flush>
          <DataTable
            columns={[
              { key: 'from', header: t('historyCol.from'), cell: (x) => (x.from ? t(`state.${x.from}`) : '—') },
              { key: 'to', header: t('historyCol.to'), cell: (x) => t(`state.${x.to}`) },
              { key: 'reason', header: t('historyCol.reason'), cell: (x) => reasonText(x.reason) },
              { key: 'at', header: t('historyCol.at'), cell: (x) => formatDateTime(x.at) },
            ] satisfies Column<SubscriptionDetail['transitions'][number]>[]}
            rows={s.transitions}
            rowKey={(x) => `${x.at}-${x.to}`}
            empty={t('detail.none')}
          />
        </Section>

        <Section title={t('detail.orders')} flush>
          <DataTable
            columns={[
              {
                key: 'order',
                header: t('orderCol.order'),
                cell: (o) => (
                  <Link className="text-primary hover:underline" to={`/orders/${encodeURIComponent(o.order_id)}`}>
                    {o.description ?? t('views.order')}
                  </Link>
                ),
              },
              { key: 'action', header: t('orderCol.action'), cell: (o) => loose(`orderAction.${o.action}`, { defaultValue: o.action }) },
              { key: 'status', header: t('orderCol.status'), cell: (o) => <Badge tone={o.status === 'FULFILLED' ? 'ok' : o.status === 'FAILED' || o.status === 'CANCELLED' ? 'neutral' : 'pending'} title={o.status}>{loose(`commerce:enum.orderItemStatus.${o.status}`, { defaultValue: o.status })}</Badge> },
              { key: 'amount', header: t('orderCol.amount'), numeric: true, cell: (o) => vnd(o.amount) },
              {
                key: 'invoice',
                header: t('orderCol.invoice'),
                cell: (o) =>
                  o.invoice ? (
                    <Link className="text-primary hover:underline" to={`/invoices/${encodeURIComponent(o.invoice.id)}`}>
                      {`${o.invoice.series}-${o.invoice.number}`}
                    </Link>
                  ) : (
                    '—'
                  ),
              },
              { key: 'at', header: t('orderCol.at'), cell: (o) => formatDateTime(o.created_at) },
            ] satisfies Column<SubscriptionDetail['orders'][number]>[]}
            rows={s.orders}
            rowKey={(o) => o.order_item_id}
            empty={t('detail.none')}
          />
        </Section>

        {s.cancellations.length ? (
          <Section title={t('detail.cancellations')} flush>
            <DataTable
              columns={[
                { key: 'timing', header: t('cancelCol.timing'), cell: (k) => t(`op.timingOption.${k.timing}`) },
                { key: 'state', header: t('cancelCol.state'), cell: (k) => loose(`cancelState.${k.state}`, { defaultValue: k.state }) },
                { key: 'reason', header: t('cancelCol.reason'), cell: (k) => k.reason },
                { key: 'at', header: t('cancelCol.at'), cell: (k) => formatDateTime(k.at) },
                { key: 'done', header: t('cancelCol.done'), cell: (k) => (k.done_at ? formatDateTime(k.done_at) : '—') },
              ] satisfies Column<SubscriptionDetail['cancellations'][number]>[]}
              rows={s.cancellations}
              rowKey={(k) => k.id}
            />
          </Section>
        ) : null}

        {s.actions.length ? (
          <Section title={t('detail.actions')} flush>
            <DataTable
              columns={[
                { key: 'kind', header: t('actionCol.kind'), cell: (a) => t(`action.${a.kind}`) },
                { key: 'reason', header: t('actionCol.reason'), cell: (a) => a.reason },
                { key: 'state', header: t('actionCol.state'), cell: (a) => <Badge tone={a.state === 'DONE' ? 'ok' : a.state === 'FAILED' ? 'crit' : 'pending'} title={a.last_error ?? a.state}>{t(`actionState.${a.state}`)}</Badge> },
                { key: 'at', header: t('actionCol.at'), cell: (a) => formatDateTime(a.created_at) },
              ] satisfies Column<SubscriptionDetail['actions'][number]>[]}
              rows={s.actions}
              rowKey={(a) => a.id}
            />
          </Section>
        ) : null}
      </div>
    </>
  );
}

/** One staff operation on a service, with its reason. */
function OperationForm({ close, changed, op, service }: DialogBodyProps & { op: ServiceOperation; service: SubscriptionDetail }) {
  const { t } = useSubT();
  const access = useAccess();
  const [reason, setReason] = useState('');
  const [days, setDays] = useState('30');
  const [why, setWhy] = useState<(typeof STAFF_SUSPEND_REASONS)[number]>('ABUSE');
  const [timing, setTiming] = useState<Timing>('END_OF_PERIOD');
  const w = useWrite((r: string, opts) => {
    switch (op) {
      case 'completeDelivery':
        return api.completeDelivery(service.id, { reason: r }, opts);
      case 'cancelDelivery':
        return api.cancelDelivery(service.id, { reason: r }, opts);
      case 'extend':
        return api.extendSubscription(service.id, { days: Number(days), reason: r }, opts);
      case 'suspend':
        return api.suspendSubscription(service.id, { suspend_reason: why, reason: r }, opts);
      case 'unsuspend':
        return api.unsuspendSubscription(service.id, { reason: r }, opts);
      case 'terminate':
        return api.terminateSubscription(service.id, { timing, reason: r }, opts);
    }
  });
  const n = Number(days);
  const daysOk = Number.isInteger(n) && n >= 1 && n <= 366 && extensionPermissions(n).every((p) => access.has([p]));
  const done = w.state.phase === 'done';
  const permission = op === 'terminate' ? 'subscription.terminate' : 'subscription.operate';
  const permitted = Boolean(access.grants?.some((g) => g.scope === 'Global' && g.permissions.includes(permission)));
  const applicable = isDeliveryOp(op) ? service.fulfilment === 'MANUAL' && service.state === 'PROVISIONING' : operationsFor(service).some((o) => o.op === op);
  const valid = permitted && applicable && reason.trim().length > 0 && reason.trim().length <= 500 && (op !== 'extend' || daysOk);
  return (
    <form
      className="grid gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (valid && !w.busy && !done) void w.run(reason.trim()).then((r) => r && changed());
      }}
    >
      {done ? (
        <Callout tone="ok">{t(op === 'completeDelivery' ? 'op.deliveryDone' : op === 'cancelDelivery' ? 'op.deliveryCancelled' : 'op.done')}</Callout>
      ) : (
        <>
          <p className="text-sm text-muted-foreground">{t(`op.${op}Intro`, { days: LARGE_EXTENSION_DAYS })}</p>
          {op === 'extend' ? (
            <Field label={t('op.days')}>
              <Input type="number" min={1} max={366} value={days} onChange={(e) => setDays(e.target.value)} />
            </Field>
          ) : null}
          {op === 'suspend' ? (
            <Field label={t('op.suspendReason')}>
              <select className={selectClass} value={why} onChange={(e) => setWhy(e.target.value as typeof why)}>
                {STAFF_SUSPEND_REASONS.map((r) => (
                  <option key={r} value={r}>
                    {t(`suspendReason.${r}`)}
                  </option>
                ))}
              </select>
            </Field>
          ) : null}
          {op === 'terminate' ? (
            <Field label={t('op.timing')}>
              <select className={selectClass} value={timing} onChange={(e) => setTiming(e.target.value as Timing)}>
                {(['END_OF_PERIOD', 'IMMEDIATE'] as const).map((x) => (
                  <option key={x} value={x}>
                    {t(`op.timingOption.${x}`)}
                  </option>
                ))}
              </select>
            </Field>
          ) : null}
          <ReasonField value={reason} onChange={setReason} />
        </>
      )}
      {w.state.phase === 'error' ? <WriteError error={w.state.error} /> : null}
      <FormFooter busy={w.busy} disabled={!valid} submit={done ? undefined : t('op.submit')} onClose={close} danger={DESTRUCTIVE_OPS.includes(op)} />
    </form>
  );
}
